if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        queueMicrotask(() => this.filterVariantMedia(
          this.elements.viewer.querySelector('.is-active[data-media-id]')?.dataset.mediaId
        ));
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      filterVariantMedia(mediaId) {
        const media = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const active = media.find((item) => item.dataset.mediaId === mediaId);
        const key = active?.dataset.variantKey || this.variantMediaKey;
        if (!key) return;
        this.variantMediaKey = key;
        const items = [...media, ...this.querySelectorAll('li[data-target]')];
        items.forEach((item) => {
          const excluded = Boolean(item.dataset.variantKey && item.dataset.variantKey !== key);
          item.hidden = excluded;
          if (excluded) {
            item.style.setProperty('display', 'none', 'important');
          } else {
            item.style.removeProperty('display');
          }
        });
        [this.elements.viewer, this.elements.thumbnails].forEach((slider) => {
          if (!slider?.slider) return;
          slider.dataset.variantFiltered = 'true';
          slider.resetPages();
        });
      }

      // The thumbnail arrows navigate the filtered images on desktop.
      updateVariantNavigation(slider) {
        if (!this.variantMediaKey || slider !== this.elements.thumbnails || !this.mql.matches) return false;
        const items = Array.from(slider.querySelectorAll('li[data-target]')).filter((item) => !item.hidden);
        const index = items.findIndex((item) => item.querySelector('[aria-current="true"]'));
        slider.prevButton.disabled = index <= 0;
        slider.nextButton.disabled = index < 0 || index >= items.length - 1;
        [slider.prevButton, slider.nextButton].forEach((button) => {
          if (items.length <= 1) button.style.setProperty('display', 'none', 'important');
          else button.style.removeProperty('display');
        });
        return true;
      }

      navigateVariantMedia(slider, direction) {
        if (!this.variantMediaKey || slider !== this.elements.thumbnails || !this.mql.matches) return false;
        const items = Array.from(slider.querySelectorAll('li[data-target]')).filter((item) => !item.hidden);
        const index = items.findIndex((item) => item.querySelector('[aria-current="true"]'));
        const target = items[index + (direction === 'next' ? 1 : -1)];
        if (target) this.setActiveMedia(target.dataset.target, false);
        return true;
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement || event.detail.currentElement.hidden || this.mql.matches) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        this.filterVariantMedia(mediaId);
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.clearTimeout(this.mediaScrollTimeout);
        this.mediaScrollTimeout = window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
            this.elements.viewer.resetPages();
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (this.variantMediaKey || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.elements.thumbnails.update();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'instant' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
